import { useEffect } from "react";
import { setSEO } from "../lib/data/seo";
import { RevealLines } from "../components/motion/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import ContactBlock from "../components/site/ContactBlock";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "../components/site/Nav";
import { Phone, Mail } from "lucide-react";

export default function Contact() {
  useEffect(() => {
    setSEO({
      title: "Contact Maze Security Solutions — British Columbia Security Services",
      description:
        "Contact Maze Security Solutions for province-wide British Columbia security services. Call (807) 907-2874 or email info@mazesecurity.ca. 24/7 dispatch.",
      keywords: ["contact BC security", "Maze Security Solutions", "security services British Columbia contact", "(807) 907-2874", "info@mazesecurity.ca"],
      canonical: `${window.location.origin}/contact`,
    });
  }, []);

  return (
    <div className="pt-24">
      <section className="pt-16 md:pt-24 pb-8 bg-[#F4F4F0]" data-testid="contact-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Contact · Talk To A Human
          </div>
          <RevealLines
            lines={["Two ways to reach us.", "Both go straight to ops."]}
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
          />
        </div>
      </section>

      <EditorialMarquee items={["CALL · TEXT · EMAIL", "24 / 7 OPERATIONS DESK", "PROVINCE-WIDE", "LICENSED · BONDED · INSURED"]} />

      {/* PRIMARY CONTACT BLOCK */}
      <section className="bg-[#F4F4F0] py-20 md:py-28" data-testid="contact-primary">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-0">
          <a
            href={PHONE_HREF}
            data-testid="contact-phone-card"
            className="group relative bg-[#060913] text-white p-10 md:p-16 border border-[#060913] hover:bg-[#D92323] hover:border-[#D92323] transition-colors flex flex-col justify-between min-h-[380px]"
          >
            <div>
              <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.28em] uppercase opacity-70">
                <Phone size={16} strokeWidth={1.5} /> Phone · 24 / 7
              </div>
              <div className="mt-8 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                {PHONE}
              </div>
            </div>
            <div className="mt-10 font-mono text-[11px] tracking-[0.22em] uppercase opacity-80 group-hover:opacity-100">
              Tap to call →
            </div>
          </a>

          <a
            href={EMAIL_HREF}
            data-testid="contact-email-card"
            className="group relative bg-white text-[#060913] p-10 md:p-16 border border-[#060913] border-t-0 md:border-t md:border-l-0 hover:bg-[#F4F4F0] transition-colors flex flex-col justify-between min-h-[380px]"
          >
            <div>
              <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                <Mail size={16} strokeWidth={1.5} /> Email · Same-Day Response
              </div>
              <div className="mt-8 font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9] break-words">
                {EMAIL}
              </div>
            </div>
            <div className="mt-10 font-mono text-[11px] tracking-[0.22em] uppercase text-[#5C5F66] group-hover:text-[#D92323]">
              Tap to email →
            </div>
          </a>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-black/10 py-20 md:py-28" data-testid="contact-process">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              How this works
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[0.95] text-[#060913]">
              Every call answered.<br/>Every email read.
            </h2>
          </div>
          <div className="md:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-black/10">
              {[
                { n: "01", t: "Call or Email", b: "Speak to an operations lead — no phone tree, no bot." },
                { n: "02", t: "Same-day response", b: "We reply the same business day, most within an hour." },
                { n: "03", t: "Site walk & scope", b: "Free on-site walk-through anywhere in BC." },
                { n: "04", t: "Transparent quote", b: "Written scope and hourly rates — no games." },
              ].map((s) => (
                <div key={s.n} className="p-6 border-b border-r border-black/10 last:border-r-0">
                  <div className="font-mono text-2xl text-[#D92323] leading-none">{s.n}</div>
                  <div className="mt-3 font-display text-xl text-[#060913]">{s.t}</div>
                  <div className="text-sm text-[#5C5F66] mt-1">{s.b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DISPATCH */}
      <section className="bg-[#F4F4F0] py-20 md:py-28" data-testid="contact-dispatch">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Emergency & Dispatch
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[#060913]">
              Alarm response, urgent coverage, after-hours — same number.
            </h2>
            <p className="mt-6 text-[#5C5F66] text-base md:text-lg leading-relaxed max-w-lg">
              Our dispatch line is live 24 / 7 for existing clients and urgent
              first-time inquiries province-wide.
            </p>
          </div>
          <div className="md:col-span-6 bg-[#060913] p-8 md:p-12">
            <ContactBlock variant="dark" testid="contact-dispatch-block" />
          </div>
        </div>
      </section>
    </div>
  );
}
